import { economyFetch as fetch } from './economyClient';
/**
 * 商人ギルド サーバー連携
 *
 * Edge Function（merchant）と連携し、
 * 商人との売買・チケット購入を行う
 */

// ========================================
// Supabase設定
// ========================================

const SUPABASE_URL = 'https://vikqkqwklbdjfhkydzpw.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZpa3FrcXdrbGJkamZoa3lkenB3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjYwNjYzODcsImV4cCI6MjA4MTY0MjM4N30.PPIf98mu1HgR95CaymSJiV9E5hnlsMJuQeXw2MNqKsw';
const AUTH_STORAGE_KEY = 'sb-vikqkqwklbdjfhkydzpw-auth-token';
const API_TIMEOUT_MS = 30000;

function getAccessToken(): string | null {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed?.access_token || null;
    }
  } catch (err) {
    console.error('[useMerchantServer] Failed to get access token:', err);
  }
  return null;
}

function withTimeout<T>(promise: Promise<T>, ms: number, operation: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error(`${operation}: タイムアウト（${ms}ms）`));
    }, ms);

    promise
      .then((result) => {
        clearTimeout(timeout);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timeout);
        reject(error);
      });
  });
}

// ========================================
// 型定義
// ========================================

export interface MerchantBuyRequest {
  merchantName: string;
  cardId: string;
  rarity: string;
  quantity: number;
  expectedPrice: number;
  dayId: number;
}

export interface MerchantBuyResponse {
  success: boolean;
  error?: string;
  purchase?: {
    cardId: string;
    rarity: string;
    quantity: number;
    totalPrice: number;
  };
  newGoldBalance?: number;
  newFavorability?: {
    transactions: number;
    level: number;
  };
}

export interface MerchantSellRequest {
  merchantName: string;
  cardId: string;
  rarity: string;
  quantity: number;
  expectedPrice: number;
}

export interface MerchantSellResponse {
  success: boolean;
  error?: string;
  sale?: {
    cardId: string;
    rarity: string;
    quantity: number;
    totalPrice: number;
  };
  newGoldBalance?: number;
  newFavorability?: {
    transactions: number;
    level: number;
  };
}

export interface TicketBuyRequest {
  ticketType: 'attribute' | 'dark' | 'traveler';
  quantity: number;
}

export interface TicketBuyResponse {
  success: boolean;
  error?: string;
  purchase?: {
    ticketType: string;
    quantity: number;
    totalPrice: number;
  };
  newGoldBalance?: number;
  newTickets?: Record<string, number>;
}

export interface MerchantStatusResponse {
  success: boolean;
  error?: string;
  merchants?: Record<string, {
    transactions: number;
    level: number;
    levelInfo: {
      required: number;
      sellDiscount: number;
      buyBonus: number;
      extraSlots?: number;
      rumorBonus?: number;
    };
  }>;
  tickets?: Record<string, number>;
  gold?: number;
}

// ========================================
// Zelvanトレード関連型定義
// ========================================

export interface CardItem {
  cardId: string;
  rarity: string;
  count: number;
}

export interface ConditionSpec {
  attribute?: string;
  rarity?: string;
  category?: string;
  maxCost?: number;
  count?: number;
}

export interface TradeOffer {
  id: string;
  type: 'equal' | 'profitable' | 'loss' | 'attribute' | 'rare' | 'gamble';
  giveType: 'specific' | 'condition';
  give: CardItem[] | null;
  giveCondition: ConditionSpec | null;
  receive: CardItem[] | null;
  receiveHidden: boolean;
  actualReceive: CardItem[] | null;
  completed: boolean;
  estimatedGiveValue: number;
  estimatedReceiveValue: number;
}

export interface ZelvanOffers {
  dayId: number;
  offers: TradeOffer[];
}

export interface ReservedOffer {
  offer: TradeOffer;
  deposit: number;
  reservedAt: number;
  expiresAt: number;
}

export interface TradeOffersResponse {
  success: boolean;
  error?: string;
  offers?: ZelvanOffers;
  reserved_offer?: ReservedOffer | null;
}

export interface TradeExecuteRequest {
  offerId: string;
  selectedCards?: Array<{ cardId: string; rarity: string }>;
  dayId: number;
}

export interface TradeExecuteResponse {
  success: boolean;
  error?: string;
  received_cards?: CardItem[];
  is_gamble?: boolean;
  stats?: {
    totalTrades?: number;
    profitableTrades?: number;
    lossTrades?: number;
    gambleWins?: number;
    gambleLosses?: number;
  };
}

export interface TradeReserveRequest {
  offerId: string;
  dayId: number;
}

export interface TradeReserveResponse {
  success: boolean;
  error?: string;
  reservation?: ReservedOffer;
  new_gold_balance?: number;
}

export interface TradeCancelResponse {
  success: boolean;
  error?: string;
  forfeited_deposit?: number;
  message?: string;
}

export interface TradeCompleteRequest {
  selectedCards?: Array<{ cardId: string; rarity: string }>;
}

export interface TradeCompleteResponse {
  success: boolean;
  error?: string;
  received_cards?: CardItem[];
  is_gamble?: boolean;
  stats?: {
    totalTrades?: number;
    profitableTrades?: number;
    lossTrades?: number;
    gambleWins?: number;
    gambleLosses?: number;
  };
}

// ========================================
// API関数
// ========================================

/**
 * 商人からカードを購入
 */
export async function buyFromMerchant(request: MerchantBuyRequest): Promise<MerchantBuyResponse> {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      console.log('[buyFromMerchant] Not authenticated, skipping');
      return { success: false, error: 'Not authenticated' };
    }

    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/merchant/buy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          merchant_name: request.merchantName,
          card_id: request.cardId,
          rarity: request.rarity,
          quantity: request.quantity,
          expected_price: request.expectedPrice,
          day_id: request.dayId,
        }),
      }),
      API_TIMEOUT_MS,
      'buyFromMerchant'
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log('[buyFromMerchant] Purchase completed:', data.purchase);

    return {
      success: data.success,
      error: data.error,
      purchase: data.purchase ? {
        cardId: data.purchase.card_id,
        rarity: data.purchase.rarity,
        quantity: data.purchase.quantity,
        totalPrice: data.purchase.total_price,
      } : undefined,
      newGoldBalance: data.new_gold_balance,
      newFavorability: data.new_favorability,
    };
  } catch (err) {
    console.error('[buyFromMerchant] Failed:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to buy from merchant',
    };
  }
}

/**
 * 商人にカードを売却
 */
export async function sellToMerchant(request: MerchantSellRequest): Promise<MerchantSellResponse> {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      console.log('[sellToMerchant] Not authenticated, skipping');
      return { success: false, error: 'Not authenticated' };
    }

    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/merchant/sell`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          merchant_name: request.merchantName,
          card_id: request.cardId,
          rarity: request.rarity,
          quantity: request.quantity,
          expected_price: request.expectedPrice,
        }),
      }),
      API_TIMEOUT_MS,
      'sellToMerchant'
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log('[sellToMerchant] Sale completed:', data.sale);

    return {
      success: data.success,
      error: data.error,
      sale: data.sale ? {
        cardId: data.sale.card_id,
        rarity: data.sale.rarity,
        quantity: data.sale.quantity,
        totalPrice: data.sale.total_price,
      } : undefined,
      newGoldBalance: data.new_gold_balance,
      newFavorability: data.new_favorability,
    };
  } catch (err) {
    console.error('[sellToMerchant] Failed:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to sell to merchant',
    };
  }
}

/**
 * チケットを購入
 */
export async function buyTicket(request: TicketBuyRequest): Promise<TicketBuyResponse> {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      console.log('[buyTicket] Not authenticated, skipping');
      return { success: false, error: 'Not authenticated' };
    }

    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/merchant/ticket/buy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          ticket_type: request.ticketType,
          quantity: request.quantity,
        }),
      }),
      API_TIMEOUT_MS,
      'buyTicket'
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log('[buyTicket] Purchase completed:', data.purchase);

    return {
      success: data.success,
      error: data.error,
      purchase: data.purchase ? {
        ticketType: data.purchase.ticket_type,
        quantity: data.purchase.quantity,
        totalPrice: data.purchase.total_price,
      } : undefined,
      newGoldBalance: data.new_gold_balance,
      newTickets: data.new_tickets,
    };
  } catch (err) {
    console.error('[buyTicket] Failed:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to buy ticket',
    };
  }
}

/**
 * 商人ステータスを取得
 */
export async function getMerchantStatus(): Promise<MerchantStatusResponse> {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      console.log('[getMerchantStatus] Not authenticated, skipping');
      return { success: false, error: 'Not authenticated' };
    }

    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/merchant/status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
      }),
      API_TIMEOUT_MS,
      'getMerchantStatus'
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log('[getMerchantStatus] Status fetched');

    return data;
  } catch (err) {
    console.error('[getMerchantStatus] Failed:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to get merchant status',
    };
  }
}

// ========================================
// Zelvanトレード API関数
// ========================================

/**
 * Zelvanオファーを取得（または生成）
 */
export async function getTradeOffers(dayId: number): Promise<TradeOffersResponse> {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      console.log('[getTradeOffers] Not authenticated, skipping');
      return { success: false, error: 'Not authenticated' };
    }

    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/merchant/trade/offers?day_id=${dayId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
      }),
      API_TIMEOUT_MS,
      'getTradeOffers'
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log('[getTradeOffers] Offers fetched:', { count: data.offers?.offers?.length });

    return data;
  } catch (err) {
    console.error('[getTradeOffers] Failed:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to get trade offers',
    };
  }
}

/**
 * トレードを実行
 */
export async function executeTrade(request: TradeExecuteRequest): Promise<TradeExecuteResponse> {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      console.log('[executeTrade] Not authenticated, skipping');
      return { success: false, error: 'Not authenticated' };
    }

    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/merchant/trade/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          offer_id: request.offerId,
          selected_cards: request.selectedCards,
          day_id: request.dayId,
        }),
      }),
      API_TIMEOUT_MS,
      'executeTrade'
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log('[executeTrade] Trade executed:', { received: data.received_cards?.length });

    return data;
  } catch (err) {
    console.error('[executeTrade] Failed:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to execute trade',
    };
  }
}

/**
 * オファーを予約
 */
export async function reserveTrade(request: TradeReserveRequest): Promise<TradeReserveResponse> {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      console.log('[reserveTrade] Not authenticated, skipping');
      return { success: false, error: 'Not authenticated' };
    }

    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/merchant/trade/reserve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          offer_id: request.offerId,
          day_id: request.dayId,
        }),
      }),
      API_TIMEOUT_MS,
      'reserveTrade'
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log('[reserveTrade] Reservation created:', data.reservation?.offer?.id);

    return data;
  } catch (err) {
    console.error('[reserveTrade] Failed:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to reserve trade',
    };
  }
}

/**
 * 予約をキャンセル
 */
export async function cancelReservation(): Promise<TradeCancelResponse> {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      console.log('[cancelReservation] Not authenticated, skipping');
      return { success: false, error: 'Not authenticated' };
    }

    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/merchant/trade/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({}),
      }),
      API_TIMEOUT_MS,
      'cancelReservation'
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log('[cancelReservation] Reservation cancelled:', data.forfeited_deposit);

    return data;
  } catch (err) {
    console.error('[cancelReservation] Failed:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to cancel reservation',
    };
  }
}

/**
 * 予約トレードを完了
 */
export async function completeReservation(request?: TradeCompleteRequest): Promise<TradeCompleteResponse> {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      console.log('[completeReservation] Not authenticated, skipping');
      return { success: false, error: 'Not authenticated' };
    }

    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/merchant/trade/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          selected_cards: request?.selectedCards,
        }),
      }),
      API_TIMEOUT_MS,
      'completeReservation'
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log('[completeReservation] Trade completed:', { received: data.received_cards?.length });

    return data;
  } catch (err) {
    console.error('[completeReservation] Failed:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to complete reservation',
    };
  }
}

// ========================================
// ヘルパー関数
// ========================================

/**
 * サーバー連携が利用可能かチェック
 */
export function isMerchantServerAvailable(): boolean {
  return getAccessToken() !== null;
}

/**
 * 日付IDを取得（UTC基準）
 */
export function getDayId(date: Date = new Date()): number {
  const utcDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  return Math.floor(utcDate.getTime() / (24 * 60 * 60 * 1000));
}

// ========================================
// エクスポート
// ========================================

export default {
  // 商人取引
  buyFromMerchant,
  sellToMerchant,
  buyTicket,
  getMerchantStatus,
  // Zelvanトレード
  getTradeOffers,
  executeTrade,
  reserveTrade,
  cancelReservation,
  completeReservation,
  // ユーティリティ
  isMerchantServerAvailable,
  getDayId,
};
